/**
 * ClimateReportBlockModal
 */

import clsx from 'clsx';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import { parseAndFormatRows } from 'common/helpers';
import { Int32MaxValue } from 'common/constants';
import { LocalizationContext } from 'context/localization.context';
import Icon from 'components/ui/Icon';
import Button from 'components/ui/Button';
import Modal from 'components/ui/Modal';
import { downloadClimateReport, ExportEpdItem } from 'api/export';
import { toast } from 'react-toastify';
import { AddToCartModel } from 'types/cart-types';

interface Props {
	/** Pass a toggle modal callback function */
	onToggle: Function;

	climateReportApiUrl: string;
}

interface ArticleProps {
	/** Item code of an article */
	code?: string;

	/** Selected quantity of an article */
	quantity?: number;
}

/** Main description for this component. */
const ClimateReportBlockModal = ({
	onToggle,
	climateReportApiUrl,
}: Props): JSX.Element => {
	const { t }: any = useContext(LocalizationContext);
	const [inputValue, setInputValue] = useState('');
	const [articles, setArticles] = useState<ArticleProps[]>([]);
	const [newRowAdded, setNewRowAdded] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const clearInput = () => {
		setInputValue('');
	};

	const handlePaste = async (e: any) => {
		const clipboardData = e.clipboardData || (window as any).clipboardData;
		const value = clipboardData.getData('Text');
		//setArticles([]);
		if (value !== '') {
			let codesAndQuantity = parseAndFormatRows(value);
			setArticles([...articles, ...codesAndQuantity]);
		}
		setInputValue('');
	};

	const exportClimateReport = async (apiUrl: string) => {
		if (!apiUrl) return;
		if (articles && articles.length > 0) {
			const validCadItems = articles.filter(
				(item) => item.code && item.quantity
			);
			if (validCadItems.length > 0) {
				let articleList: ExportEpdItem[] = [];
				validCadItems.forEach((articleItem: ArticleProps) => {
					articleList.push({
						itemCode: articleItem.code!,
						quantity: articleItem.quantity!,
					});
				});
				try {
					setIsLoading(true);
					await downloadClimateReport(apiUrl, articleList);
				} catch (err) {
					toast('Export failed', { type: toast.TYPE.ERROR });
				} finally {
					setIsLoading(false);
				}
			}
		}
	};

	const data = useMemo(() => articles, [articles]);
	const columns: any = useMemo(
		() => [
			{ Header: t('quickadd/itemcode'), accessor: 'code' },
			{ Header: t('quickadd/quantity'), accessor: 'quantity' },
		],
		//eslint-disable-next-line
		[]
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
	} = useTable({
		columns,
		data,
	});

	const handleAddRow = () => {
		setArticles([
			...articles,
			{
				code: '',
			},
		]);

		setNewRowAdded(true);
	};

	const handleInputChange = (e: any, cell: any) => {
		let value;
		let newState: ArticleProps[] = [];
		switch (cell.column.id) {
			case 'quantity':
				value = e.target.value || undefined;
				if (isNaN(value) || value === '' || value < 1) {
					value = undefined;
				} else if (value > Int32MaxValue) {
					value = Int32MaxValue;
				}
				newState = [...articles];
				newState[cell.row.id].quantity = value ? parseInt(value) : value;
				setArticles(newState);
				break;

			case 'code':
				value = e.target.value;
				newState = [...articles];
				newState[cell.row.id].code = value;
				newState[cell.row.id].quantity = newState[cell.row.id].quantity ?? 1;
				setArticles(newState);
				break;

			default:
				break;
		}
	};

	const handleKeyDown = (e: any) => {
		if (e.key === 'Enter') {
			handleAddRow();
		}
	};

	const handleRemoveRow = (index: number) => {
		const newArticles = articles.filter((item, i) => {
			return i !== index;
		});

		setArticles(newArticles);
	};

	//Focus new row when created.
	useEffect(() => {
		if (newRowAdded) {
			const newInput = document.querySelector(
				`#rowinput-${data.length - 1}-code`
			);
			(newInput as HTMLElement)?.focus();
			setNewRowAdded(false);
		}
		//eslint-disable-next-line
	}, [newRowAdded]);

	return (
		<Modal
			toggleModal={() => onToggle && onToggle()}
			heading={t('climatereport/exportclimatereport')}
			title={t('climatereport/exportclimatereport')}
			width="max"
		>
			<div>
				<label htmlFor={'pasteinput'} className="text-p block m-0">
					{t('quickadd/importdescription')}
				</label>
				<input
					id="pasteinput"
					name="pasteinput"
					value={inputValue}
					onBlur={() => clearInput()}
					onChange={() => {}}
					onPaste={handlePaste}
					placeholder={t('quickadd/importpasteplaceholder')}
					className="text-p py-2 px-4 my-4 w-full md:w-6/12 h-10 rounded border border-greyDark"
				/>
			</div>
			<div>
				<table className="w-full quickimporttable" {...getTableProps()}>
					<caption></caption>
					<thead className="bg-blue">
						{headerGroups.map((headerGroup: any, index: number) => (
							<tr
								key={index}
								className="text-white text-p p-2"
								{...headerGroup.getHeaderGroupProps()}
							>
								<th className="text-sm p-1 max-w-16 md:max-w-25 lg:max-w-none md:text-p md:py-2 md:px-4 font-medium text-center whitespace-no-wrap overflow-hidden overflow-ellipsis">
									{t('quickadd/remove')}
								</th>

								{headerGroup.headers.map((column: any, index: number) => (
									<th
										key={index}
										{...column.getHeaderProps()}
										id={`header-${index}`}
										className="text-sm p-1 text-left max-w-16 md:max-w-25 lg:max-w-none md:text-p md:py-2 md:px-4 font-medium whitespace-no-wrap overflow-hidden overflow-ellipsis"
									>
										{column.render('Header')}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{rows.map((row: any, index: number) => {
							prepareRow(row);
							return (
								<tr className={clsx('border-b border-white')} key={index}>
									<td className="py-2 px-4">
										<button
											className="bg-transparent w-full"
											onClick={() => handleRemoveRow(index)}
										>
											<Icon
												icon="clear"
												aria-label={t('quickadd/removerow')}
												color="#949494"
											/>
										</button>
									</td>
									{row.cells.map((cell: any, index: number) => {
										return (
											<td {...cell.getCellProps()}>
												{cell.column.id === 'quantity' ? (
													<input
														type="number"
														id={`rowinput-${row.id}-${cell.column.id}`}
														className="text-p py-2 px-4 w-full bg-transparent rounded border-none"
														aria-labelledby={`header-${index}`}
														value={cell.value || ''}
														onChange={(e) => handleInputChange(e, cell)}
														onKeyDown={handleKeyDown}
													/>
												) : cell.column.id === 'code' ? (
													<input
														type="text"
														id={`rowinput-${row.id}-${cell.column.id}`}
														className="text-p py-2 px-4 w-full bg-transparent rounded border-none"
														aria-labelledby={`header-${index}`}
														value={cell.value || ''}
														onChange={(e) => handleInputChange(e, cell)}
														onKeyDown={handleKeyDown}
													/>
												) : (
													<span className="text-p py-2 px-4">
														{cell.render('Cell')}
													</span>
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
				<Button buttonColor="grey" type="button" onClick={handleAddRow}>
					<Icon icon="plus" className="mr-2.5 ml-0 align-text-top" />
					{t('quickadd/addrow')}
				</Button>
				{climateReportApiUrl && articles?.some((item) => item.code) && (
					<Button
						buttonColor="blue"
						type="button"
						className="ml-4"
						disabled={isLoading}
						onClick={() => {
							exportClimateReport(climateReportApiUrl);
						}}
					>
						{t('climatereport/exportclimatereport')}
						{isLoading && (
							<Icon icon="loader" size={1} animate="spin" className="ml-2" />
						)}
					</Button>
				)}
			</div>
		</Modal>
	);
};

export default ClimateReportBlockModal;
